/**
 * `GET /organizations/{id}/projects` — the projects in an organisation the
 * signed-in user can see. Ported from `bimgo-ai-web`'s `useProject` query.
 *
 * Unlike the members endpoints this one answers the **envelope**, `{ data: … }`,
 * as `/organizations` does — api-v2 is not uniform about it, so each client says
 * which shape it expects rather than sharing a guess.
 *
 * The web calls api-v2 directly (Corrections #3): the renderer owns the Supabase
 * session, so it is the only thing holding a token.
 */
import { OrganizationsError, readDetail } from "./organizations";
import { bearer } from "./accessToken";
import { apiFetch } from "./apiFetch";

export interface Project {
  id: string;
  name: string;
  description?: string;
  notes?: string;
  organization_id: string;
  user_id: string;
  /** Active/inactive — a bool in api-v2's schema, not a status string. */
  status: boolean;
  created_at: string;
  updated_at: string;
  /**
   * Computed server-side on the list endpoint (org admin / creator / assigned
   * project_admin), and **absent on create/update responses**. Treat "missing"
   * as owner so behaviour only tightens once every deployment sends it; api-v2
   * enforces regardless.
   */
  is_owner?: boolean;
}

export async function getProjects(
  token: string | null,
  orgId: string,
  deps: { apiBaseUrl: string; fetchImpl?: typeof fetch },
): Promise<Project[]> {
  if (!token) throw new OrganizationsError({ kind: "unauthenticated" }, "not signed in");

  const fetchImpl = deps.fetchImpl ?? apiFetch;
  const url = `${deps.apiBaseUrl.replace(/\/+$/, "")}/organizations/${encodeURIComponent(orgId)}/projects`;

  let response: Response;
  try {
    response = await fetchImpl(url, { headers: { Authorization: bearer(token) } });
  } catch (error) {
    throw new OrganizationsError({ kind: "network", detail: String(error) }, String(error));
  }

  if (response.status === 401 || response.status === 403) {
    throw new OrganizationsError({ kind: "unauthenticated" }, "session rejected by the API");
  }

  const body: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const detail = readDetail(body);
    throw new OrganizationsError(
      { kind: "http", status: response.status, detail },
      detail ?? `HTTP ${response.status}`,
    );
  }

  const data = (body as { data?: unknown } | null)?.data;
  return Array.isArray(data) ? (data as Project[]) : [];
}

export interface CreateProjectInput {
  organization_id: string;
  name: string;
  description?: string;
  notes?: string;
}

export type UpdateProjectInput = Partial<Pick<Project, "name" | "description" | "notes">>;

/**
 * Shared request path for the two mutations.
 *
 * Both answer `{ success, data: [project] }` — the created/updated row wrapped in
 * an **array of one**, not the bare object the field name suggests. Reading it as
 * an object yields `undefined` and a list that silently fails to update.
 */
async function mutate(
  method: "POST" | "PUT",
  url: string,
  token: string | null,
  payload: unknown,
  fetchImpl: typeof fetch,
): Promise<Project | null> {
  if (!token) throw new OrganizationsError({ kind: "unauthenticated" }, "not signed in");

  let response: Response;
  try {
    response = await fetchImpl(url, {
      method,
      headers: {
        Authorization: bearer(token),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
  } catch (error) {
    throw new OrganizationsError({ kind: "network", detail: String(error) }, String(error));
  }

  if (response.status === 401 || response.status === 403) {
    throw new OrganizationsError({ kind: "unauthenticated" }, "session rejected by the API");
  }

  const body: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const detail = readDetail(body);
    throw new OrganizationsError(
      { kind: "http", status: response.status, detail },
      detail ?? `HTTP ${response.status}`,
    );
  }

  const data = (body as { data?: unknown } | null)?.data;
  return Array.isArray(data) && data.length > 0 ? (data[0] as Project) : null;
}

/**
 * `POST /projects/` — **with the trailing slash**, which api-v2 declares and
 * FastAPI would otherwise answer with a 307 to the slashed form. A redirect is
 * how the `Authorization` header quietly goes missing, so the slash is part of
 * the contract rather than a typo.
 */
export async function createProject(
  token: string | null,
  input: CreateProjectInput,
  deps: { apiBaseUrl: string; fetchImpl?: typeof fetch },
): Promise<Project | null> {
  const url = `${deps.apiBaseUrl.replace(/\/+$/, "")}/projects`;
  return mutate("POST", url, token, input, deps.fetchImpl ?? apiFetch);
}

/**
 * `DELETE /projects/{id}` — the one call here that destroys data.
 *
 * Separate from `mutate` because it sends no body, and because a `DELETE` that
 * quietly reports success on a 4xx is worse than one that throws: the row would
 * vanish from the list on the refetch and reappear on the next load. Every
 * non-2xx raises, so the caller can leave its dialog open and say why.
 */
export async function deleteProject(
  token: string | null,
  projectId: string,
  deps: { apiBaseUrl: string; fetchImpl?: typeof fetch },
): Promise<void> {
  if (!token) throw new OrganizationsError({ kind: "unauthenticated" }, "not signed in");

  const fetchImpl = deps.fetchImpl ?? apiFetch;
  const url = `${deps.apiBaseUrl.replace(/\/+$/, "")}/projects/${encodeURIComponent(projectId)}`;

  let response: Response;
  try {
    response = await fetchImpl(url, {
      method: "DELETE",
      headers: { Authorization: bearer(token) },
    });
  } catch (error) {
    throw new OrganizationsError({ kind: "network", detail: String(error) }, String(error));
  }

  if (response.status === 401 || response.status === 403) {
    throw new OrganizationsError({ kind: "unauthenticated" }, "session rejected by the API");
  }

  if (!response.ok) {
    const body: unknown = await response.json().catch(() => null);
    const detail = readDetail(body);
    throw new OrganizationsError(
      { kind: "http", status: response.status, detail },
      detail ?? `HTTP ${response.status}`,
    );
  }
}

/** `PUT /projects/{id}` — no trailing slash here, unlike create. */
export async function updateProject(
  token: string | null,
  projectId: string,
  input: UpdateProjectInput,
  deps: { apiBaseUrl: string; fetchImpl?: typeof fetch },
): Promise<Project | null> {
  const url = `${deps.apiBaseUrl.replace(/\/+$/, "")}/projects/${encodeURIComponent(projectId)}`;
  return mutate("PUT", url, token, input, deps.fetchImpl ?? apiFetch);
}
